import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Tag, Space,
  Popconfirm, message, Alert, Typography, Badge
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, StarOutlined, StarFilled, BankOutlined
} from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const BANK_LIST = [
  { code: 'BCA', name: 'BCA - Bank Central Asia' },
  { code: 'BNI', name: 'BNI - Bank Negara Indonesia' },
  { code: 'BRI', name: 'BRI - Bank Rakyat Indonesia' },
  { code: 'MANDIRI', name: 'Bank Mandiri' },
  { code: 'BSI', name: 'BSI - Bank Syariah Indonesia' },
  { code: 'CIMB', name: 'CIMB Niaga' },
  { code: 'DANAMON', name: 'Bank Danamon' },
  { code: 'PERMATA', name: 'Bank Permata' },
  { code: 'BTN', name: 'BTN - Bank Tabungan Negara' },
  { code: 'MEGA', name: 'Bank Mega' },
  { code: 'OCBC', name: 'OCBC NISP' },
  { code: 'PANIN', name: 'Bank Panin' },
  { code: 'MAYBANK', name: 'Maybank Indonesia' },
  { code: 'OTHER', name: 'Bank Lainnya' },
];

const verificationColor = { pending: 'orange', verified: 'green', rejected: 'red' };
const verificationLabel = { pending: 'Menunggu Verifikasi', verified: 'Terverifikasi', rejected: 'Ditolak' };

const MerchantBankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { fetchAccounts(); }, []);

  const getToken = () => localStorage.getItem('merchant_access_token');

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/merchant/bank-accounts', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAccounts(res.data?.data || []);
    } catch (err) {
      message.error('Gagal memuat daftar rekening');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values) => {
    setLoading(true);
    try {
      await api.post('/merchant/bank-accounts', values, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      message.success('Rekening berhasil ditambahkan, menunggu verifikasi admin');
      setModalVisible(false);
      form.resetFields();
      fetchAccounts();
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal menambahkan rekening');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await api.put(`/merchant/bank-accounts/${id}/set-primary`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      message.success('Rekening utama diperbarui');
      fetchAccounts();
    } catch (err) {
      message.error('Gagal mengubah rekening utama');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/merchant/bank-accounts/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      message.success('Rekening berhasil dihapus');
      fetchAccounts();
    } catch (err) {
      message.error('Gagal menghapus rekening');
    }
  };

  const columns = [
    {
      title: 'Bank',
      key: 'bank',
      render: (_, r) => (
        <Space>
          <BankOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{r.bank_name}</div>
            {r.branch_name && <div style={{ fontSize: 12, color: '#888' }}>{r.branch_name}</div>}
          </div>
          {r.is_primary ? <Tag color="blue" icon={<StarFilled />}>Utama</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Nomor Rekening',
      key: 'account',
      render: (_, r) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 14 }}>{r.account_number}</div>
          <div style={{ color: '#555' }}>{r.account_name}</div>
        </div>
      )
    },
    {
      title: 'Status Verifikasi',
      dataIndex: 'verification_status',
      key: 'verification_status',
      render: (s) => <Tag color={verificationColor[s]}>{verificationLabel[s]}</Tag>
    },
    {
      title: 'Aksi',
      key: 'actions',
      render: (_, r) => (
        <Space>
          {!r.is_primary && r.is_verified ? (
            <Button size="small" icon={<StarOutlined />} onClick={() => handleSetPrimary(r.id)}>
              Jadikan Utama
            </Button>
          ) : null}
          <Popconfirm
            title="Hapus rekening ini?"
            onConfirm={() => handleDelete(r.id)}
            okText="Ya" cancelText="Tidak"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Hapus</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={<Space><BankOutlined /><span>Rekening Bank Withdrawal</span></Space>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            Tambah Rekening
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Rekening baru perlu diverifikasi admin sebelum bisa digunakan untuk withdrawal."
          style={{ marginBottom: 16 }}
        />
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Belum ada rekening terdaftar' }}
        />
      </Card>

      <Modal
        title="Tambah Rekening Bank"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item name="bank_name" label="Bank" rules={[{ required: true, message: 'Pilih bank' }]}>
            <Select
              showSearch
              placeholder="Pilih bank"
              optionFilterProp="children"
              onChange={(val) => {
                const bank = BANK_LIST.find(b => b.name === val);
                if (bank) form.setFieldsValue({ bank_code: bank.code });
              }}
            >
              {BANK_LIST.map(b => <Option key={b.code} value={b.name}>{b.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="bank_code" hidden><Input /></Form.Item>
          <Form.Item
            name="account_number"
            label="Nomor Rekening"
            rules={[
              { required: true, message: 'Masukkan nomor rekening' },
              { pattern: /^\d+$/, message: 'Hanya angka' }
            ]}
          >
            <Input placeholder="1234567890" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="account_name"
            label="Nama Pemilik Rekening"
            rules={[{ required: true, message: 'Masukkan nama pemilik rekening' }]}
          >
            <Input placeholder="Sesuai buku tabungan" />
          </Form.Item>
          <Form.Item name="branch_name" label="Cabang (opsional)">
            <Input placeholder="Contoh: KCP Bandung Kota" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }}>Batal</Button>
              <Button type="primary" htmlType="submit" loading={loading}>Simpan</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MerchantBankAccounts;
